/**
 * useClassificationBanner.ts
 *
 * React Query hook for fetching active classification banner configuration.
 *
 * Features:
 * - Fetches active banner config from GET /v1/classification/active
 * - Caches result with 5 minute stale time
 * - Only renders banner if module is enabled AND assignment exists
 * - Returns formatted banner data with colors and display options
 *
 * Usage:
 * ```tsx
 * const { data, isLoading, error } = useClassificationBanner();
 *
 * if (data?.banner?.enabled) {
 *   // Render banner with data.banner
 * }
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/base/utils/api';

// ============================================================================
// Types
// ============================================================================

export interface ClassificationBannerConfig {
  enabled: boolean;
  showTopBanner: boolean;
  showBottomBanner: boolean;
  bannerLine: string;
  backgroundColor: string;
  textColor: string;
  systemName: string | null;
  environmentTag: string | null;
  fullText: string;
  screenReaderText: string;
}

export interface ClassificationBannerResponse {
  banner: ClassificationBannerConfig | null;
}

// ============================================================================
// API Function
// ============================================================================

/**
 * Fetch active classification banner configuration
 */
async function fetchClassificationBanner(): Promise<ClassificationBannerResponse> {
  const response = await apiClient.get<ClassificationBannerResponse>(
    '/v1/classification/active'
  );
  return response.data;
}

// ============================================================================
// Hook
// ============================================================================

export interface UseClassificationBannerResult {
  /** Active banner configuration (null if disabled or no assignment) */
  banner: ClassificationBannerConfig | null;
  /** Whether the module is enabled and banner should display */
  enabled: boolean;
  /** Banner classification marking text (e.g., "SECRET//NOFORN") */
  bannerLine: string | null;
  /** Background color hex code */
  backgroundColor: string | null;
  /** Text color hex code */
  textColor: string | null;
  /** Whether to show top banner */
  showTopBanner: boolean;
  /** Whether to show bottom banner */
  showBottomBanner: boolean;
  /** Full banner text including system/environment tags */
  fullText: string | null;
  /** Screen reader friendly text */
  screenReaderText: string | null;
  /** Loading state */
  isLoading: boolean;
  /** Error if fetch failed */
  error: Error | null;
}

/**
 * Hook to fetch and manage classification banner configuration.
 *
 * This hook fetches the active classification banner from the backend and
 * provides a convenient interface for accessing banner configuration.
 *
 * The banner will only be enabled if:
 * 1. The classification-banners module is enabled
 * 2. An active assignment exists for the current context
 *
 * @returns Classification banner configuration and loading state
 */
export function useClassificationBanner(): UseClassificationBannerResult {
  const { data, isLoading, error } = useQuery<ClassificationBannerResponse>({
    queryKey: ['classification-banner', 'active'],
    queryFn: fetchClassificationBanner,
    // Cache for 5 minutes since classification rarely changes
    staleTime: 5 * 60 * 1000,
    // Retry once on failure
    retry: 1,
    // Don't retry on 404 (no assignment exists)
    retryOnMount: false,
  });

  const banner = data?.banner ?? null;
  const enabled = banner?.enabled ?? false;

  return {
    banner,
    enabled,
    bannerLine: banner?.bannerLine ?? null,
    backgroundColor: banner?.backgroundColor ?? null,
    textColor: banner?.textColor ?? null,
    showTopBanner: banner?.showTopBanner ?? false,
    showBottomBanner: banner?.showBottomBanner ?? false,
    fullText: banner?.fullText ?? null,
    screenReaderText: banner?.screenReaderText ?? null,
    isLoading,
    error: error as Error | null,
  };
}

export default useClassificationBanner;
